/**
 * Wave 5 — the rule set is the workspace. Where a set's rules live in the console, and where
 * the pre-Wave-5 addresses (`/rules/transaction?set=…&rule=…`) go now.
 *
 *   /rule-sets                          the list
 *   /rule-sets/<set>/transaction        the set's single-transaction rules (RB)
 *   /rule-sets/<set>/pattern            the set's pattern rules (PB)
 *
 * Pure: no React, no router — the routes, the redirects and every link builder use these.
 */

export type RuleFamily = 'rb' | 'pb';

export const RULE_SETS_PATH = '/rule-sets';

/** The path segment of a family's tab. */
export function familySegment(kind: RuleFamily): 'transaction' | 'pattern' {
  return kind === 'rb' ? 'transaction' : 'pattern';
}

/** `PB-…` is a pattern rule; anything else opens on Transaction (the screen hands it over if wrong). */
export function familyOfRuleId(ruleId: string): RuleFamily {
  return /^PB-/i.test(ruleId) ? 'pb' : 'rb';
}

/** One set's rules of one family, optionally opening a rule (`?rule=`). */
export function setRulesPath(setId: string, kind: RuleFamily, ruleId?: string): string {
  const base = `${RULE_SETS_PATH}/${encodeURIComponent(setId)}/${familySegment(kind)}`;
  return ruleId ? `${base}?rule=${encodeURIComponent(ruleId)}` : base;
}

/**
 * The pre-Wave-5 rule screens: `/rules/transaction[?set=x][&rule=y]` →
 * `/rule-sets/<x or the primary>/transaction[?rule=y]`, same for `/rules/pattern`. Any other
 * query parameter is kept. Null while the set is not named and the primary is not known yet.
 */
export function legacyRulesRedirect(kind: RuleFamily, search: string, primaryId: string | null | undefined): string | null {
  const params = new URLSearchParams(search);
  const set = params.get('set') || primaryId;
  if (!set) return null;
  params.delete('set');
  const rest = params.toString();
  return `${setRulesPath(set, kind)}${rest ? `?${rest}` : ''}`;
}
